import { useTranslation } from 'react-i18next'
import {
  Archive,
  Briefcase,
  ExternalLink,
  MoreVertical,
  Pause,
  Pencil,
  UserPlus,
  Zap,
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { IconTile } from '@/components/ui/icon-tile'
import { EntityCardIdentity } from '@/components/app/EntityCardIdentity'
import { cn } from '@/lib/utils'
import { useNavigate } from 'react-router-dom'

interface AssignAgentDemoDialogProps {
  open: boolean
  onClose: () => void
}

const STEP_KEYS = ['step1', 'step2', 'step3', 'step4'] as const

export function AssignAgentDemoDialog({ open, onClose }: AssignAgentDemoDialogProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const handleGoToPrograms = () => {
    onClose()
    navigate('/programs')
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose() }}>
      <DialogContent className="max-w-[440px] gap-0 p-0 overflow-hidden">

        {/* ── Header ── */}
        <div className="bg-amber-400 px-5 py-4">
          <div className="flex items-start gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius)] bg-amber-950/12 ring-1 ring-amber-950/10">
              <Zap className="h-4 w-4 text-amber-950" />
            </div>
            <div className="min-w-0 pt-0.5">
              <DialogTitle className="text-sm font-bold leading-tight text-amber-950">
                {t('onboarding.steps.assign.label')}
              </DialogTitle>
              <DialogDescription className="mt-0.5 text-[11px] leading-relaxed text-amber-900/80">
                {t('onboarding.assignDemo.subtitle')}
              </DialogDescription>
            </div>
          </div>
        </div>

        <div className="px-5 py-4 space-y-4">

          {/* ── Demo card with open dropdown ── */}
          <div className="relative pb-2">
            <Card className="w-full overflow-visible border-border shadow-sm">

              {/* Card header row */}
              <div className="flex items-start justify-between gap-2 px-3 pt-3 pb-1 sm:px-4">
                <EntityCardIdentity
                  leading={<IconTile icon={Briefcase} size="md" className="bg-blue-500 text-white" />}
                  title={t('onboarding.assignDemo.demoName')}
                  description={t('onboarding.assignDemo.demoDesc')}
                  badge={
                    <Badge
                      variant="secondary"
                      className="border-0 text-[10px] bg-emerald-500/15 text-emerald-800 dark:text-emerald-300"
                    >
                      {t('programs.status.active')}
                    </Badge>
                  }
                  className="flex-1 min-w-0"
                  titleClassName="!text-sm text-foreground"
                  descriptionClassName="text-xs"
                />

                {/* ⋯ button — "pressed" state */}
                <div className="relative shrink-0">
                  <div className="flex h-8 w-8 items-center justify-center rounded-[var(--radius)] bg-accent ring-1 ring-border text-foreground">
                    <MoreVertical className="size-4" />
                  </div>

                  {/* Fake-open dropdown panel */}
                  <div
                    className="absolute right-0 top-[calc(100%+4px)] z-50 w-52 overflow-hidden rounded-[var(--radius)] border border-border bg-popover py-1 text-popover-foreground shadow-xl"
                    style={{ boxShadow: '0 10px 40px -4px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.04)' }}
                  >
                    {/* Dimmed menu items above the target */}
                    {[
                      { Icon: ExternalLink, label: t('programs.card.viewProgram') },
                      { Icon: Pencil, label: t('programs.card.edit') },
                      { Icon: Pause, label: t('programs.card.pause') },
                    ].map(({ Icon, label }) => (
                      <div
                        key={label}
                        className="flex select-none items-center gap-2.5 px-3 py-1.5 text-xs text-muted-foreground/50"
                      >
                        <Icon className="h-3.5 w-3.5 shrink-0" />
                        <span>{label}</span>
                      </div>
                    ))}

                    {/* Separator */}
                    <div className="my-1 h-px bg-border" />

                    {/* ★ Highlighted "Assign agents" row */}
                    <div className="flex select-none items-center gap-2.5 bg-amber-400 px-3 py-2 text-xs font-semibold text-amber-950">
                      <UserPlus className="h-3.5 w-3.5 shrink-0" />
                      <span>{t('programs.card.assignAgents')}</span>
                    </div>

                    {/* Separator */}
                    <div className="my-1 h-px bg-border" />

                    {/* Dimmed item below */}
                    <div className="flex select-none items-center gap-2.5 px-3 py-1.5 text-xs text-muted-foreground/50">
                      <Archive className="h-3.5 w-3.5 shrink-0" />
                      <span>{t('programs.card.archive')}</span>
                    </div>
                  </div>
                </div>
              </div>

              <CardContent className="space-y-2 px-3 pb-3 pt-1 sm:px-4">
                {/* Meta grid */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-[var(--radius)] border border-dashed border-border/60 bg-muted/10 px-3 py-2">
                    <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                      {t('programs.card.meta.attribution')}
                    </p>
                    <p className="mt-1 text-xs font-semibold text-foreground">
                      {t('programs.card.roleSummary.perTransactionValue', { count: '100' })}
                    </p>
                  </div>
                  <div className="rounded-[var(--radius)] border border-dashed border-border/60 bg-muted/10 px-3 py-2">
                    <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                      {t('programs.card.meta.points')}
                    </p>
                    <p className="mt-1 text-xs font-medium text-foreground">
                      {t('programs.card.exchangeMode.reward')}
                    </p>
                  </div>
                </div>

                {/* Assignments — amber empty-state to draw eye */}
                <div className="rounded-[var(--radius)] border-2 border-dashed border-amber-400/50 bg-amber-50/50 px-3 py-2.5 dark:bg-amber-950/10">
                  <p className="text-[10px] uppercase tracking-[0.14em] text-amber-700 dark:text-amber-400">
                    {t('programs.card.assignments.summary', { count: 0 })}
                  </p>
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-dashed border-amber-400/60 bg-amber-100/80 dark:bg-amber-900/20">
                      <UserPlus className="h-3 w-3 text-amber-500" />
                    </div>
                    <p className="text-xs font-medium text-amber-700 dark:text-amber-400">
                      {t('programs.card.assignments.none')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* ── Vertical timeline steps ── */}
          <div className="relative pl-1">
            {/* Connecting line */}
            <div className="absolute left-[10px] top-4 bottom-4 w-px bg-border" />

            <ol className="relative space-y-3">
              {STEP_KEYS.map((key, idx) => {
                const isActive = idx === 1
                return (
                  <li key={key} className="flex items-start gap-3">
                    <span
                      className={cn(
                        'relative z-10 mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[9px] font-bold',
                        isActive
                          ? 'bg-amber-400 text-amber-950 ring-2 ring-amber-200 dark:ring-amber-900/60'
                          : 'bg-background border border-border text-muted-foreground',
                      )}
                    >
                      {idx + 1}
                    </span>
                    <p
                      className={cn(
                        'pt-0.5 text-xs leading-relaxed',
                        isActive ? 'font-medium text-foreground' : 'text-muted-foreground',
                      )}
                    >
                      {t(`onboarding.assignDemo.${key}`)}
                    </p>
                  </li>
                )
              })}
            </ol>
          </div>

          {/* ── CTA ── */}
          <Button
            type="button"
            className="w-full gap-2 font-semibold"
            onClick={handleGoToPrograms}
          >
            <Briefcase className="h-4 w-4" />
            {t('onboarding.assignDemo.cta')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
